import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Shield, Users, Building2, BookOpen, DollarSign, Loader2, Save, Send, Eye,
  CheckCircle, AlertCircle, Mail, UserPlus, Activity, CreditCard, Globe,
  HelpCircle, FileWarning, BarChart3, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, X, Check, Clock,
  Trash2, Edit2, Plus, ArrowLeft, Link as LinkIcon, MessageSquare, Search, Edit, LogOut, Store, Lock, Star, Flag, Ban, Smartphone, TrendingUp
} from 'lucide-react';
import { UsersSecuritySection } from '@/pages/platform-admin/UsersSecuritySection';
import { AnalyticsSection } from '@/pages/platform-admin/AnalyticsSection';
import { WebsiteSettingsSection } from '@/pages/platform-admin/WebsiteSettingsSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Use admin token for platform admin API calls
const api = async (path, options = {}) => {
  const token = localStorage.getItem('ihostpms_admin_token');
  if (!token) {
    window.location.href = '/admin/login';
    throw new Error('Not authenticated');
  }
  const resp = await fetch(`${BACKEND_URL}/api/admin${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers },
  });
  const text = await resp.text();
  if (!resp.ok) {
    if (resp.status === 401) {
      localStorage.removeItem('ihostpms_admin_token');
      localStorage.removeItem('ihostpms_admin');
      window.location.href = '/admin/login';
    }
    let errorMsg = text;
    try {
      const errJson = JSON.parse(text);
      errorMsg = errJson.detail || errJson.message || text;
    } catch {}
    throw new Error(errorMsg);
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const StatCard = ({ label, value, icon: Icon, color, sub }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-200/60">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
    </div>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

const ADMIN_SECTIONS = [
  { id: 'dashboard', label: 'Dashboard', icon: Activity },
  { id: 'tenants', label: 'Tenants', icon: Users },
  { id: 'plans', label: 'Subscription Plans', icon: CreditCard },
  { id: 'payments', label: 'Payment Config', icon: DollarSign },
  { id: 'channels', label: 'Channel Partners', icon: LinkIcon },
  { id: 'mobile-app', label: 'Mobile App', icon: Smartphone },
  { id: 'moderation', label: 'Reviews & Testimonials', icon: Flag },
  { id: 'users-security', label: 'Users & Security', icon: Shield },
  { id: 'support', label: 'Support Sessions', icon: UserPlus },
  { id: 'website-settings', label: 'Website Settings', icon: Globe },
  { id: 'faqs', label: 'FAQs & Docs', icon: HelpCircle },
  { id: 'deployment', label: 'Deployment Guide', icon: BookOpen },
  { id: 'errors', label: 'Error Logs', icon: FileWarning },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'audit', label: 'Audit Log', icon: Shield },
];

const PlatformAdminPage = () => {
  const { user } = useAuth();
  const [section, setSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [partners, setPartners] = useState([]);
  const [team, setTeam] = useState([]);
  const [smtp, setSmtp] = useState({ host: '', port: 587, username: '', password: '', from_email: '', from_name: 'iHostPMS', use_tls: true, start_tls: false });
  const [website, setWebsite] = useState({});
  const [mpesa, setMpesa] = useState({ consumer_key: '', consumer_secret: '', shortcode: '', passkey: '', environment: 'sandbox', callback_url: '' });
  const [mpesaSaving, setMpesaSaving] = useState(false);
  const [pesapal, setPesapal] = useState({ consumer_key: '', consumer_secret: '', environment: 'sandbox', ipn_id: '' });
  const [pesapalSaving, setPesapalSaving] = useState(false);
  const [pesapalTestResult, setPesapalTestResult] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [errors, setErrors] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [audit, setAudit] = useState([]);
  const [supportSessions, setSupportSessions] = useState([]);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpResult, setSmtpResult] = useState(null);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [webSaving, setWebSaving] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('');
  const [mobileSidebar, setMobileSidebar] = useState(false);

  // Dialogs
  const [approveDialog, setApproveDialog] = useState(null);
  const [approvalPlan, setApprovalPlan] = useState('starter');
  const [approvalTrialDays, setApprovalTrialDays] = useState(14);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'support' });
  const [faqDialog, setFaqDialog] = useState(null);
  // Login As dialog
  const [loginAsDialog, setLoginAsDialog] = useState(null);
  const [loginAsReason, setLoginAsReason] = useState('Support request');
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: 'general', sort_order: 0 });
  const [detailDialog, setDetailDialog] = useState(null);
  // New dialogs for editing
  const [editTenantDialog, setEditTenantDialog] = useState(null);
  const [tenantEditForm, setTenantEditForm] = useState({ name: '', plan: '', status: '', admin_notes: '' });
  const [editPlanDialog, setEditPlanDialog] = useState(null);
  const [planForm, setPlanForm] = useState({ name: '', code: '', price: 0, max_users: 1, max_properties: 1, features: [], trial_days: 14, is_active: true });
  const [newFeature, setNewFeature] = useState('');
  const [legalTab, setLegalTab] = useState('privacy');
  const [websiteTab, setWebsiteTab] = useState('config');

  // Pagination state
  const [tenantPage, setTenantPage] = useState(1);
  const [tenantsPerPage, setTenantsPerPage] = useState(10);

  // Available features for licensing
  const AVAILABLE_FEATURES = [
    { id: 'properties', name: 'Property Management', description: 'Create and manage properties' },
    { id: 'bookings', name: 'Booking Management', description: 'Handle guest bookings' },
    { id: 'calendar', name: 'Availability Calendar', description: 'Manage property availability' },
    { id: 'channel_sync', name: 'OTA Channel Sync', description: 'Sync with Airbnb, Booking.com, etc.' },
    { id: 'messaging', name: 'Guest Messaging', description: 'In-app messaging with guests' },
    { id: 'dynamic_pricing', name: 'Dynamic Pricing', description: 'AI-powered price optimization' },
    { id: 'ab_testing', name: 'A/B Price Testing', description: 'Test different pricing strategies' },
    { id: 'demand_forecast', name: 'Demand Forecasting', description: '90-day demand predictions' },
    { id: 'reports', name: 'Reports & Analytics', description: 'Revenue and occupancy reports' },
    { id: 'reviews', name: 'Review Management', description: 'Guest review collection and responses' },
    { id: 'widgets', name: 'Booking Widgets', description: 'Embeddable booking widgets' },
    { id: 'team_members', name: 'Team Members', description: 'Invite and manage team' },
    { id: 'api_access', name: 'API Access', description: 'Developer API access' },
    { id: 'white_label', name: 'White Label', description: 'Custom branding options' },
    { id: 'priority_support', name: 'Priority Support', description: '24/7 priority customer support' },
  ];

  // Security / Password Policy state
  const [passwordPolicy, setPasswordPolicy] = useState({
    min_length: 8,
    require_uppercase: false,
    require_lowercase: false,
    require_numbers: false,
    require_special: false
  });
  const [securitySaving, setSecuritySaving] = useState(false);
  const [securitySaved, setSecuritySaved] = useState(false);

  // Stripe state
  const [stripe, setStripe] = useState({ publishable_key: '', secret_key: '', webhook_secret: '', environment: 'test' });
  const [stripeSaving, setStripeSaving] = useState(false);

  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialCounts, setTestimonialCounts] = useState({ pending: 0, approved: 0, featured: 0, total: 0 });
  const [testimonialFilter, setTestimonialFilter] = useState('all');

  // Content Moderation state
  const [flaggedReviews, setFlaggedReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [moderationFilter, setModerationFilter] = useState('all');
  const [moderationSearch, setModerationSearch] = useState('');
  const [moderationTab, setModerationTab] = useState('reviews'); // 'reviews' or 'testimonials'
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [spamStats, setSpamStats] = useState(null);
  const [scanningSpam, setScanningSpam] = useState(false);

  const loadSection = useCallback(async (s) => {
    try {
      switch (s) {
        case 'dashboard': setDashboard(await api('/dashboard')); break;
        case 'tenants': setTenants(await api('/tenants')); break;
        case 'plans': setPlans(await api('/plans')); break;
        case 'channels': setPartners(await api('/channel-partners')); break;
        case 'team': setTeam(await api('/team')); break;
        case 'smtp': setSmtp(await api('/smtp')); break;
        case 'website': setWebsite(await api('/website-config')); break;
        case 'payments':
          try {
            const mpesaResp = await fetch(`${BACKEND_URL}/api/payments/mpesa/config`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('ihostpms_admin_token')}` }
            });
            if (mpesaResp.ok) setMpesa(await mpesaResp.json());
          } catch {}
          try {
            const pesapalResp = await fetch(`${BACKEND_URL}/api/payment-gateways/platform`, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('ihostpms_admin_token')}` }
            });
            if (pesapalResp.ok) {
              const data = await pesapalResp.json();
              if (data.pesapal) setPesapal(data.pesapal);
              if (data.stripe) setStripe(data.stripe);
            }
          } catch {}
          setTransactions(await api('/transactions'));
          break;
        case 'faqs': setFaqs(await api('/faqs')); break;
        case 'errors': setErrors(await api('/error-logs?limit=50')); break;
        case 'analytics': setAnalytics(await api('/analytics')); break;
        case 'audit': setAudit(await api('/audit-log?limit=50')); break;
        case 'support': setSupportSessions(await api('/support-sessions')); break;
        case 'users-security':
          // Load team and security settings
          setTeam(await api('/team'));
          try {
            const settings = await api('/settings');
            if (settings.password_policy) {
              setPasswordPolicy(settings.password_policy);
            }
          } catch {}
          break;
        case 'website-settings':
          // Load SMTP and website config
          setSmtp(await api('/smtp'));
          setWebsite(await api('/website-config'));
          break;
        case 'moderation':
          try {
            const token = localStorage.getItem('ihostpms_admin_token');
            // Load all reviews across all tenants for moderation
            const reviewsResp = await fetch(`${BACKEND_URL}/api/admin/moderation/reviews`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (reviewsResp.ok) {
              const data = await reviewsResp.json();
              setAllReviews(data.reviews || []);
              setFlaggedReviews(data.reviews?.filter(r => r.flagged) || []);
            }
            // Also load testimonials
            const testimonialsResp = await fetch(`${BACKEND_URL}/api/testimonials/admin`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (testimonialsResp.ok) {
              const data = await testimonialsResp.json();
              setTestimonials(data.testimonials || []);
              setTestimonialCounts(data.counts || { pending: 0, approved: 0, featured: 0, total: 0 });
            }
          } catch (err) { console.error('Moderation load error:', err); }
          break;
        default: break;
      }
    } catch (err) { console.error('Load error:', err); }
  }, []);

  useEffect(() => {
    const init = async () => {
      // Check if admin is logged in
      const adminToken = localStorage.getItem('ihostpms_admin_token');
      if (!adminToken) {
        window.location.href = '/admin/login';
        return;
      }
      try { setDashboard(await api('/dashboard')); }
      catch (err) { 
        console.error(err);
        // If auth fails, redirect to login
        if (err.message?.includes('401') || err.message?.includes('Not authenticated')) {
          window.location.href = '/admin/login';
        }
      }
      finally { setLoading(false); }
    };
    init();
  }, []);

  useEffect(() => { if (!loading) loadSection(section); }, [section, loading, loadSection]);

  const handleApproveDecline = async (action) => {
    if (!approveDialog) return;
    setProcessing(true);
    try {
      await api(`/tenants/${approveDialog.tenant_id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ action, plan_code: approvalPlan, trial_days: approvalTrialDays, notes: approvalNotes })
      });
      setApproveDialog(null);
      setApprovalNotes('');
      loadSection('tenants');
      loadSection('dashboard');
    } catch (err) { console.error(err); }
    finally { setProcessing(false); }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await api('/team/invite', { method: 'POST', body: JSON.stringify(newMember) });
      setInviteDialog(false);
      setNewMember({ name: '', email: '', role: 'support' });
      loadSection('team');
    } catch (err) { console.error(err); }
    finally { setProcessing(false); }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await api(`/team/${userId}`, { method: 'DELETE' });
      loadSection('team');
    } catch (err) { console.error(err); }
  };

  const handleSaveSMTP = async () => {
    setSmtpSaving(true);
    try { await api('/smtp', { method: 'PUT', body: JSON.stringify(smtp) }); }
    catch (err) { console.error(err); }
    finally { setSmtpSaving(false); }
  };

  const handleTestSMTP = async () => {
    setSmtpTesting(true);
    setSmtpResult(null);
    try { 
      const r = await api('/smtp/test', { 
        method: 'POST',
        body: JSON.stringify({ recipient_email: testEmailRecipient || '' })
      }); 
      setSmtpResult(r.success ? { status: 'ok', message: r.message } : { status: 'fail', message: r.message || 'Failed to send' }); 
    }
    catch (err) { setSmtpResult({ status: 'fail', message: err.message || 'Failed to send' }); }
    finally { setSmtpTesting(false); }
  };

  const handleSaveWebsite = async () => {
    setWebSaving(true);
    try { await api('/website-config', { method: 'PUT', body: JSON.stringify(website) }); }
    catch (err) { console.error(err); }
    finally { setWebSaving(false); }
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('ihostpms_admin_token');
    localStorage.removeItem('ihostpms_admin');
    window.location.href = '/admin/login';
  };

  const handleSaveMpesa = async () => {
    setMpesaSaving(true);
    try { 
      await fetch(`${BACKEND_URL}/api/payments/mpesa/config`, { 
        method: 'PUT', 
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('ihostpms_admin_token')}` },
        body: JSON.stringify(mpesa) 
      }); 
    }
    catch (err) { console.error(err); }
    finally { setMpesaSaving(false); }
  };

  const handleSavePesapal = async () => {
    setPesapalSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/payment-gateways/platform`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('ihostpms_admin_token')}` },
        body: JSON.stringify({
          stripe: { enabled: !!stripe.secret_key, ...stripe },
          mpesa: { enabled: !!mpesa.consumer_key, ...mpesa },
          pesapal: { enabled: !!pesapal.consumer_key, ...pesapal }
        })
      });
      setPesapalTestResult({ success: true, message: 'Pesapal configuration saved' });
      setTimeout(() => setPesapalTestResult(null), 3000);
    } catch (err) {
      setPesapalTestResult({ success: false, message: 'Failed to save Pesapal configuration' });
      console.error(err);
    } finally {
      setPesapalSaving(false);
    }
  };

  const handleSaveStripe = async () => {
    setStripeSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/payment-gateways/platform`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('ihostpms_admin_token')}` },
        body: JSON.stringify({
          stripe: { enabled: !!stripe.secret_key, ...stripe },
          mpesa: { enabled: !!mpesa.consumer_key, ...mpesa },
          pesapal: { enabled: !!pesapal.consumer_key, ...pesapal }
        })
      });
      alert('Stripe configuration saved successfully');
    } catch (err) {
      alert('Failed to save Stripe configuration');
      console.error(err);
    } finally {
      setStripeSaving(false);
    }
  };

  const handleTestPesapal = async () => {
    if (!pesapal.consumer_key || !pesapal.consumer_secret) {
      setPesapalTestResult({ success: false, message: 'Please enter consumer key and secret first' });
      return;
    }
    setPesapalTestResult({ loading: true, message: 'Testing connection...' });
    try {
      // First save, then test
      await handleSavePesapal();
      const resp = await fetch(`${BACKEND_URL}/api/payment-gateways/pesapal/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('ihostpms_admin_token')}` }
      });
      const data = await resp.json();
      setPesapalTestResult(data);
    } catch (err) {
      setPesapalTestResult({ success: false, message: 'Connection test failed: ' + err.message });
    }
  };

  const handleSaveFAQ = async () => {
    setProcessing(true);
    try {
      if (faqDialog === 'new') {
        await api('/faqs', { method: 'POST', body: JSON.stringify(faqForm) });
      } else {
        await api(`/faqs/${faqDialog}`, { method: 'PUT', body: JSON.stringify(faqForm) });
      }
      setFaqDialog(null);
      loadSection('faqs');
    } catch (err) { console.error(err); }
    finally { setProcessing(false); }
  };

  const handleDeleteFAQ = async (id) => {
    try { await api(`/faqs/${id}`, { method: 'DELETE' }); loadSection('faqs'); }
    catch (err) { console.error(err); }
  };

  // Tenant Edit handlers
  const openTenantEdit = async (tenant) => {
    // Ensure plans are loaded before opening dialog
    if (plans.length === 0) {
      try {
        const loadedPlans = await api('/plans');
        setPlans(loadedPlans);
      } catch (err) {
        console.error('Failed to load plans:', err);
      }
    }
    setTenantEditForm({
      name: tenant.name || '',
      plan: tenant.plan || 'starter',
      status: tenant.status || 'active',
      admin_notes: ''
    });
    setEditTenantDialog(tenant);
  };

  // Review Dialog handler - ensure plans are loaded
  const openReviewDialog = async (tenant) => {
    if (plans.length === 0) {
      try {
        const loadedPlans = await api('/plans');
        setPlans(loadedPlans);
      } catch (err) {
        console.error('Failed to load plans:', err);
      }
    }
    setApprovalPlan('starter');
    setApprovalTrialDays(14);
    setApprovalNotes('');
    setApproveDialog(tenant);
  };

  // Delete tenant handler
  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this declined tenant? This action cannot be undone.')) return;
    setProcessing(true);
    try {
      await api(`/tenants/${tenantId}`, { method: 'DELETE' });
      loadSection('tenants');
      loadSection('dashboard');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to delete tenant');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveTenant = async () => {
    if (!editTenantDialog) return;
    setProcessing(true);
    try {
      await api(`/tenants/${editTenantDialog.tenant_id}`, {
        method: 'PUT',
        body: JSON.stringify(tenantEditForm)
      });
      setEditTenantDialog(null);
      loadSection('tenants');
    } catch (err) { 
      console.error(err);
      alert(err.message || 'Failed to update tenant');
    }
    finally { setProcessing(false); }
  };

  // Plan Edit handlers
  const openPlanEdit = (plan) => {
    setPlanForm({
      name: plan.name || '',
      code: plan.code || '',
      price: plan.price || 0,
      currency: plan.currency || 'KES',
      max_users: plan.max_users || 1,
      max_properties: plan.max_properties || 1,
      features: plan.features || [],
      licensed_features: plan.licensed_features || [],
      trial_days: plan.trial_days || 14,
      billing_cycle: plan.billing_cycle || 'monthly',
      is_active: plan.is_active !== false,
      is_contact_us: plan.is_contact_us || false
    });
    setEditPlanDialog(plan);
  };

  const handleSavePlan = async () => {
    if (!editPlanDialog) return;
    setProcessing(true);
    try {
      await api(`/plans/${editPlanDialog.code}`, {
        method: 'PUT',
        body: JSON.stringify(planForm)
      });
      setEditPlanDialog(null);
      loadSection('plans');
    } catch (err) { 
      console.error(err);
      alert(err.message || 'Failed to update plan');
    }
    finally { setProcessing(false); }
  };

  const addFeatureToPlan = () => {
    if (newFeature.trim()) {
      setPlanForm(prev => ({ ...prev, features: [...prev.features, newFeature.trim()] }));
      setNewFeature('');
    }
  };

  const removeFeatureFromPlan = (index) => {
    setPlanForm(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  // Login As handler
  const handleLoginAs = async () => {
    if (!loginAsDialog) return;
    setProcessing(true);
    try {
      const result = await api('/login-as', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: loginAsDialog.tenant_id, reason: loginAsReason })
      });
      
      // Store the support session info
      localStorage.setItem('ihostpms_support_session', JSON.stringify({
        session_id: result.session_id,
        tenant: result.tenant,
        admin_token: localStorage.getItem('ihostpms_admin_token')
      }));
      
      // Switch to tenant token
      localStorage.setItem('ihostpms_token', result.token);
      localStorage.setItem('ihostpms_user', JSON.stringify(result.user));
      
      // Navigate to tenant dashboard
      window.open('/dashboard', '_blank');
      
      setLoginAsDialog(null);
      setLoginAsReason('Support request');
      loadSection('support');
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to login as tenant');
    }
    finally { setProcessing(false); }
  };

  // Removed role check - admin auth is handled by API and redirect
  if (loading) return <div className="flex items-center justify-center py-20 min-h-screen bg-slate-900"><Loader2 className="w-10 h-10 text-emerald-400 animate-spin" /></div>;

  // Filter tenants by search
  const searchFilteredTenants = tenants.filter(t =>
    t.name?.toLowerCase().includes(tenantFilter.toLowerCase()) ||
    t.owner_email?.toLowerCase().includes(tenantFilter.toLowerCase()) ||
    t.status?.toLowerCase().includes(tenantFilter.toLowerCase())
  );

  // Pagination calculations
  const totalTenants = searchFilteredTenants.length;
  const totalPages = Math.ceil(totalTenants / tenantsPerPage);
  const startIdx = (tenantPage - 1) * tenantsPerPage;
  const filteredTenants = searchFilteredTenants.slice(startIdx, startIdx + tenantsPerPage);

  // Reset page when filter changes
  const handleTenantFilterChange = (value) => {
    setTenantFilter(value);
    setTenantPage(1);
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-amber-100 text-amber-700',
      active: 'bg-emerald-100 text-emerald-700',
      declined: 'bg-red-100 text-red-700',
      suspended: 'bg-slate-100 text-slate-600',
    };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${map[status] || 'bg-slate-100 text-slate-600'}`}>{status || 'unknown'}</span>;
  };

  return (
    <div data-testid="platform-admin-page" className="animate-fade-in min-h-screen bg-slate-100">
      <div className="flex min-h-screen">
        {/* Platform Sidebar - Dark Theme */}
        <aside className={`
          fixed lg:static inset-0 z-50 w-64 bg-slate-900 text-white flex flex-col
          transition-transform lg:translate-x-0
          ${mobileSidebar ? 'translate-x-0' : '-translate-x-full'}
        `} data-testid="platform-sidebar">
          <div className="px-5 py-4 border-b border-slate-700/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-slate-300" />
              <span className="font-bold text-sm tracking-wide">PLATFORM ADMIN</span>
            </div>
            <button onClick={() => setMobileSidebar(false)} className="lg:hidden"><X className="w-5 h-5" /></button>
          </div>
          <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
            <a href="/dashboard" className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-white/5 hover:text-slate-200 transition-all mb-2" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </a>
            {ADMIN_SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => { setSection(s.id); setMobileSidebar(false); }}
                data-testid={`platform-nav-${s.id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${
                  section === s.id
                    ? 'bg-white/10 text-white font-medium'
                    : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                }`}
              >
                <s.icon className="w-4 h-4" />{s.label}
                {s.id === 'tenants' && dashboard?.pending_tenants > 0 && (
                  <span className="ml-auto bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{dashboard.pending_tenants}</span>
                )}
              </button>
            ))}
            
            {/* Marketplace Admin - Separate Page */}
            <div className="pt-3 mt-3 border-t border-slate-700/50">
              <a
                href="/admin/marketplace"
                data-testid="marketplace-admin-link"
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 transition-all"
              >
                <Store className="w-4 h-4" /> Marketplace (iStayBnB)
              </a>
            </div>
          </nav>
          {/* Logout Button */}
          <div className="p-3 border-t border-slate-700/50">
            <button 
              onClick={handleAdminLogout}
              data-testid="admin-logout-btn"
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </aside>
        {mobileSidebar && <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setMobileSidebar(false)} />}

        {/* Main Content - Light Theme */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto bg-slate-100">
          <button onClick={() => setMobileSidebar(true)} className="lg:hidden mb-3 flex items-center gap-2 text-sm text-slate-500">
            <Shield className="w-4 h-4" /> Platform Menu
          </button>

          {/* ─── DASHBOARD ─── */}
          {section === 'dashboard' && dashboard && (
            <div className="space-y-6" data-testid="platform-dashboard">
              <h2 className="text-xl font-bold text-slate-800">Platform Overview</h2>

              {/* ── QUICK ACTIONS ── */}
              {dashboard.quick_actions && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="quick-actions-panel">
                  {/* Pending Approvals */}
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200/60 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
                          <Clock className="w-4.5 h-4.5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-amber-900">Pending Approvals</p>
                          <p className="text-xs text-amber-600">{dashboard.pending_tenants} awaiting review</p>
                        </div>
                      </div>
                      {dashboard.pending_tenants > 0 && (
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-7 rounded-lg"
                          onClick={() => setSection('tenants')} data-testid="quick-review-tenants">
                          Review
                        </Button>
                      )}
                    </div>
                    {dashboard.quick_actions.pending_tenant_list?.length > 0 ? (
                      <div className="space-y-1.5">
                        {dashboard.quick_actions.pending_tenant_list.slice(0, 3).map(t => (
                          <div key={t.tenant_id} className="flex items-center justify-between bg-white/60 rounded-lg px-3 py-1.5">
                            <div>
                              <p className="text-xs font-medium text-amber-900">{t.name}</p>
                              <p className="text-[10px] text-amber-600">{t.owner_email}</p>
                            </div>
                            <span className="text-[10px] text-amber-500">{t.created_at?.split('T')[0]}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-500 italic">No pending approvals</p>
                    )}
                  </div>

                  {/* APK Downloads */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/60 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
                        <Smartphone className="w-4.5 h-4.5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-blue-900">APK Downloads</p>
                        <p className="text-xs text-blue-600">{dashboard.quick_actions.apk_downloads?.recent_7d || 0} this week</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-800">{dashboard.quick_actions.apk_downloads?.host || 0}</p>
                        <p className="text-[10px] text-blue-500 font-medium">Host App</p>
                      </div>
                      <div className="bg-white/60 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-indigo-800">{dashboard.quick_actions.apk_downloads?.guest || 0}</p>
                        <p className="text-[10px] text-indigo-500 font-medium">Guest App</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-3 text-xs h-7 border-blue-200 text-blue-700 hover:bg-blue-100"
                      onClick={() => setSection('mobile-app')} data-testid="quick-view-downloads">
                      Manage Apps
                    </Button>
                  </div>

                  {/* System Health */}
                  <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border border-emerald-200/60 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center">
                        <Activity className="w-4.5 h-4.5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-emerald-900">System Health</p>
                        <p className="text-xs text-emerald-600">All systems operational</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between bg-white/60 rounded-lg px-3 py-2">
                        <span className="text-xs text-emerald-800">API Status</span>
                        <span className="text-xs font-medium text-emerald-600 flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                          {dashboard.quick_actions.system_health?.api_status || 'operational'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between bg-white/60 rounded-lg px-3 py-2">
                        <span className="text-xs text-emerald-800">Database</span>
                        <span className="text-xs font-medium text-emerald-600 flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-emerald-500" />
                          {dashboard.quick_actions.system_health?.db_status || 'connected'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between bg-white/60 rounded-lg px-3 py-2">
                        <span className="text-xs text-emerald-800">Errors (24h)</span>
                        <span className={`text-xs font-bold ${(dashboard.quick_actions.system_health?.errors_24h || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {dashboard.quick_actions.system_health?.errors_24h || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-between bg-white/60 rounded-lg px-3 py-2">
                        <span className="text-xs text-emerald-800">Widgets / Views</span>
                        <span className="text-xs font-medium text-emerald-700">
                          {dashboard.quick_actions.system_health?.total_widgets || 0} / {dashboard.quick_actions.system_health?.total_widget_views || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard label="Total Tenants" value={dashboard.total_tenants} icon={Users} color="bg-slate-800" />
                <StatCard label="Pending Approvals" value={dashboard.pending_tenants} icon={Clock} color="bg-amber-500" sub="Awaiting review" />
                <StatCard label="Active Tenants" value={dashboard.active_tenants} icon={CheckCircle} color="bg-emerald-500" />
                <StatCard label="Total Users" value={dashboard.total_users} icon={Users} color="bg-blue-500" />
                <StatCard label="Properties" value={dashboard.total_properties} icon={Building2} color="bg-purple-500" />
                <StatCard label="Bookings" value={dashboard.total_bookings} icon={BookOpen} color="bg-indigo-500" />
                <StatCard label="Revenue" value={`KES ${dashboard.total_revenue?.toLocaleString()}`} icon={DollarSign} color="bg-emerald-600" />
                <StatCard label="New (7d)" value={dashboard.recent_registrations_7d} icon={Activity} color="bg-sky-500" sub="Last 7 days" />
              </div>
              {Object.keys(dashboard.tenants_by_plan || {}).length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200/60 p-5">
                  <h3 className="text-sm font-bold text-slate-700 mb-3">Tenants by Plan</h3>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(dashboard.tenants_by_plan).map(([plan, count]) => (
                      <div key={plan} className="text-center">
                        <p className="text-2xl font-bold text-slate-800">{count}</p>
                        <p className="text-xs text-slate-500 capitalize">{plan}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── TENANTS ─── */}
          {section === 'tenants' && (
            <div className="space-y-4" data-testid="platform-tenants">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">Tenant Management</h2>
                <div className="flex items-center gap-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs rounded-xl"
                    onClick={async () => {
                      setProcessing(true);
                      try {
                        await api('/tenants/refresh-stats', { method: 'POST' });
                        loadSection('tenants');
                        loadSection('dashboard');
                      } catch (err) {
                        console.error('Failed to refresh stats:', err);
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing}
                    data-testid="refresh-stats-btn"
                  >
                    {processing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Activity className="w-3.5 h-3.5 mr-1" />}
                    Refresh Stats
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs rounded-xl text-amber-600 border-amber-200 hover:bg-amber-50"
                    onClick={async () => {
                      if (!window.confirm('This will suspend tenants whose trial expired > 30 days ago and never paid. Continue?')) return;
                      setProcessing(true);
                      try {
                        const res = await api('/tenants/cleanup-dormant', { method: 'POST', body: JSON.stringify({ dormant_days: 30 }) });
                        const data = typeof res === 'string' ? JSON.parse(res) : res;
                        alert(data.message || 'Cleanup complete');
                        loadSection('tenants');
                      } catch (err) { alert(err.message || 'Cleanup failed'); }
                      finally { setProcessing(false); }
                    }}
                    disabled={processing}
                    data-testid="cleanup-dormant-btn"
                  >
                    Cleanup Dormant
                  </Button>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="tenant-search" placeholder="Search tenants..." value={tenantFilter} onChange={e => handleTenantFilterChange(e.target.value)} className="pl-9 h-9 w-56 text-sm" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200/60 overflow-x-auto">
                <table className="w-full" data-testid="platform-tenants-table">
                  <thead><tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase">Tenant</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase">Owner</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">Plan</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">Props</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-semibold text-slate-500 uppercase">Users</th>
                    <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-slate-500 uppercase">Actions</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredTenants.map(t => (
                      <tr key={t.tenant_id} className="hover:bg-slate-50/50" data-testid={`platform-tenant-${t.tenant_id}`}>
                        <td className="px-4 py-2.5"><div className="text-sm font-medium text-slate-800">{t.name}</div><div className="text-[10px] text-slate-400">{t.tenant_id}</div></td>
                        <td className="px-4 py-2.5"><div className="text-sm text-slate-700">{t.owner_name}</div><div className="text-[10px] text-slate-400">{t.owner_email}</div></td>
                        <td className="px-4 py-2.5 text-center">{statusBadge(t.status)}</td>
                        <td className="px-4 py-2.5 text-center text-sm capitalize">{t.plan}</td>
                        <td className="px-4 py-2.5 text-center text-sm font-medium">{t.property_count}</td>
                        <td className="px-4 py-2.5 text-center text-sm font-medium">{t.user_count}</td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {t.status === 'pending' && (
                              <Button variant="ghost" size="sm" className="text-xs text-amber-600 hover:bg-amber-50 h-7" onClick={() => openReviewDialog(t)} data-testid={`review-${t.tenant_id}`}>
                                <Clock className="w-3.5 h-3.5 mr-1" /> Review
                              </Button>
                            )}
                            {t.status === 'active' && (
                              <Button variant="ghost" size="sm" className="text-xs h-7 text-emerald-600 hover:bg-emerald-50" onClick={() => setLoginAsDialog(t)} data-testid={`login-as-${t.tenant_id}`}>
                                <UserPlus className="w-3.5 h-3.5 mr-1" /> Login As
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="text-xs h-7 text-blue-600 hover:bg-blue-50" onClick={() => openTenantEdit(t)} data-testid={`edit-${t.tenant_id}`}>
                              <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                            </Button>
                            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={async () => { setDetailDialog(await api(`/tenants/${t.tenant_id}`)); }} data-testid={`view-${t.tenant_id}`}>
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            {t.status === 'declined' && (
                              <Button variant="ghost" size="sm" className="text-xs h-7 text-red-600 hover:bg-red-50" onClick={() => handleDeleteTenant(t.tenant_id)} data-testid={`delete-${t.tenant_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredTenants.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">No tenants found</div>}
              </div>
              
              {/* Pagination Controls */}
              <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200/60 px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-slate-500">Show</span>
                  <select 
                    value={tenantsPerPage} 
                    onChange={e => { setTenantsPerPage(Number(e.target.value)); setTenantPage(1); }}
                    className="h-8 px-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="tenants-per-page"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-slate-500">per page</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">
                    Showing {startIdx + 1}-{Math.min(startIdx + tenantsPerPage, totalTenants)} of {totalTenants}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setTenantPage(1)} 
                      disabled={tenantPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setTenantPage(p => Math.max(1, p - 1))} 
                      disabled={tenantPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 text-sm font-medium">Page {tenantPage} of {totalPages || 1}</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setTenantPage(p => Math.min(totalPages, p + 1))} 
                      disabled={tenantPage >= totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setTenantPage(totalPages)} 
                      disabled={tenantPage >= totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── SUBSCRIPTION PLANS ─── */}
          {section === 'plans' && (
            <div className="space-y-6" data-testid="platform-plans">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Subscription Plans</h2>
                <p className="text-sm text-slate-500">Manage subscription plans and feature licensing. Click Edit to modify pricing and features.</p>
              </div>
              
              {/* Plan Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {plans.map(p => (
                  <div key={p.plan_id} className={`bg-white rounded-2xl border p-5 relative ${p.code === 'standard' ? 'border-slate-800 ring-1 ring-slate-800' : 'border-slate-200/60'}`} data-testid={`plan-${p.code}`}>
                    {p.code === 'standard' && <p className="text-[10px] font-bold text-slate-800 uppercase mb-2">Most Popular</p>}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="absolute top-3 right-3 text-xs h-7 text-blue-600 hover:bg-blue-50"
                      onClick={() => openPlanEdit(p)}
                      data-testid={`edit-plan-${p.code}`}
                    >
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <h3 className="text-lg font-bold text-slate-800">{p.name}</h3>
                    <div className="mt-2">
                      {p.is_contact_us ? (
                        <p className="text-sm text-slate-500 font-medium">Contact Us</p>
                      ) : (
                        <p className="text-2xl font-bold text-slate-800">KES {p.price?.toLocaleString()}<span className="text-sm font-normal text-slate-400">/mo</span></p>
                      )}
                    </div>
                    <div className="mt-3 space-y-1 text-xs text-slate-600">
                      <p className="font-medium">{p.max_users === 999 ? 'Unlimited' : p.max_users} user{p.max_users > 1 ? 's' : ''} &middot; {p.max_properties === 999 ? 'Unlimited' : p.max_properties} {p.max_properties > 1 ? 'properties' : 'property'}</p>
                      {p.licensed_features?.slice(0, 4).map((f, i) => (
                        <div key={i} className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-500" />{AVAILABLE_FEATURES.find(af => af.id === f)?.name || f}</div>
                      ))}
                      {p.licensed_features?.length > 4 && <p className="text-slate-400 font-medium">+{p.licensed_features.length - 4} more features</p>}
                    </div>
                    {p.trial_days > 0 && <p className="text-[10px] text-slate-400 mt-3">{p.trial_days}-day free trial</p>}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-[10px] text-slate-400">{p.licensed_features?.length || 0} features</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Feature Licensing Reference */}
              <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Available Features for Licensing</h3>
                <p className="text-sm text-slate-500 mb-4">These features can be enabled or disabled per plan. Tenants will only have access to features included in their subscription plan.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {AVAILABLE_FEATURES.map(feature => (
                    <div key={feature.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                      <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{feature.name}</p>
                        <p className="text-xs text-slate-500">{feature.description}</p>
                        <p className="text-[10px] text-slate-400 mt-1">ID: {feature.id}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}


          {/* ─── PAYMENT CONFIG ─── */}
          {section === 'payments' && (
            <div className="space-y-6" data-testid="platform-payments">
              <h2 className="text-xl font-bold text-slate-800">Payment Configuration</h2>

              {/* Stripe */}
              <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center"><CreditCard className="w-5 h-5 text-purple-600" /></div>
                  <div><h3 className="font-bold text-slate-800">Stripe (Card Payments)</h3><p className="text-xs text-slate-400">Accept credit/debit card payments worldwide</p></div>
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold ${stripe.secret_key ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {stripe.secret_key ? 'Configured' : 'Not Configured'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Publishable Key</Label>
                    <Input 
                      data-testid="stripe-pk" 
                      value={stripe.publishable_key || ''} 
                      onChange={e => setStripe(s => ({...s, publishable_key: e.target.value}))} 
                      placeholder="pk_test_..." 
                      className="mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Secret Key</Label>
                    <Input 
                      data-testid="stripe-sk" 
                      type="password" 
                      value={stripe.secret_key || ''} 
                      onChange={e => setStripe(s => ({...s, secret_key: e.target.value}))} 
                      placeholder="sk_test_..." 
                      className="mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Webhook Secret (optional)</Label>
                    <Input 
                      data-testid="stripe-webhook" 
                      type="password" 
                      value={stripe.webhook_secret || ''} 
                      onChange={e => setStripe(s => ({...s, webhook_secret: e.target.value}))} 
                      placeholder="whsec_..." 
                      className="mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Environment</Label>
                    <select 
                      data-testid="stripe-env" 
                      value={stripe.environment || 'test'} 
                      onChange={e => setStripe(s => ({...s, environment: e.target.value}))} 
                      className="w-full h-9 mt-1 px-3 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="test">Test Mode</option>
                      <option value="live">Live Mode</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <Button onClick={handleSaveStripe} disabled={stripeSaving} className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl" data-testid="save-stripe-btn">
                    <Save className="w-4 h-4 mr-2" /> Save Stripe Config
                  </Button>
                  <p className="text-xs text-slate-400">Test keys are prefixed with pk_test_ and sk_test_</p>
                </div>
              </div>

              {/* M-Pesa */}
              <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 110 20 10 10 0 010-20zm0 2a8 8 0 100 16 8 8 0 000-16zm-1 3h2v2h-2V7zm0 4h2v6h-2v-6z"/></svg>
                  </div>
                  <div><h3 className="font-bold text-slate-800">M-Pesa (Daraja API)</h3><p className="text-xs text-slate-400">Accept M-Pesa mobile money payments via STK Push</p></div>
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold ${mpesa.consumer_key ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {mpesa.consumer_key ? 'Configured' : 'Not Configured'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-sm">Consumer Key</Label><Input data-testid="mpesa-key" value={mpesa.consumer_key} onChange={e => setMpesa(m => ({...m, consumer_key: e.target.value}))} placeholder="Your Daraja Consumer Key" className="mt-1" /></div>
                  <div><Label className="text-sm">Consumer Secret</Label><Input data-testid="mpesa-secret" type="password" value={mpesa.consumer_secret} onChange={e => setMpesa(m => ({...m, consumer_secret: e.target.value}))} placeholder="Your Daraja Consumer Secret" className="mt-1" /></div>
                  <div><Label className="text-sm">Business Shortcode</Label><Input data-testid="mpesa-shortcode" value={mpesa.shortcode} onChange={e => setMpesa(m => ({...m, shortcode: e.target.value}))} placeholder="174379 (sandbox)" className="mt-1" /></div>
                  <div><Label className="text-sm">Lipa na M-Pesa Passkey</Label><Input data-testid="mpesa-passkey" type="password" value={mpesa.passkey} onChange={e => setMpesa(m => ({...m, passkey: e.target.value}))} placeholder="Your Passkey" className="mt-1" /></div>
                  <div>
                    <Label className="text-sm">Environment</Label>
                    <select data-testid="mpesa-env" value={mpesa.environment} onChange={e => setMpesa(m => ({...m, environment: e.target.value}))} className="w-full h-9 mt-1 px-3 border border-slate-200 rounded-lg text-sm">
                      <option value="sandbox">Sandbox (Testing)</option>
                      <option value="production">Production (Live)</option>
                    </select>
                  </div>
                  <div><Label className="text-sm">Callback URL (optional)</Label><Input value={mpesa.callback_url} onChange={e => setMpesa(m => ({...m, callback_url: e.target.value}))} placeholder="Auto-detected if empty" className="mt-1" /></div>
                </div>
                <Button onClick={handleSaveMpesa} disabled={mpesaSaving} className="mt-4 bg-slate-800 hover:bg-slate-900 text-white rounded-xl" data-testid="save-mpesa-btn">
                  <Save className="w-4 h-4 mr-2" /> Save M-Pesa Config
                </Button>
              </div>

              {/* Pesapal */}
              <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">Pesapal</h3>
                    <p className="text-xs text-slate-400">Multi-channel payments (cards, M-Pesa, bank transfers) across Africa</p>
                  </div>
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold ${pesapal.consumer_key ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {pesapal.consumer_key ? 'Configured' : 'Not Configured'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Consumer Key</Label>
                    <Input 
                      data-testid="pesapal-key" 
                      value={pesapal.consumer_key || ''} 
                      onChange={e => setPesapal(p => ({...p, consumer_key: e.target.value}))} 
                      placeholder="From Pesapal Dashboard" 
                      className="mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Consumer Secret</Label>
                    <Input 
                      data-testid="pesapal-secret" 
                      type="password" 
                      value={pesapal.consumer_secret || ''} 
                      onChange={e => setPesapal(p => ({...p, consumer_secret: e.target.value}))} 
                      placeholder="From Pesapal Dashboard" 
                      className="mt-1" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Environment</Label>
                    <select 
                      data-testid="pesapal-env" 
                      value={pesapal.environment || 'sandbox'} 
                      onChange={e => setPesapal(p => ({...p, environment: e.target.value}))} 
                      className="w-full h-9 mt-1 px-3 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="sandbox">Sandbox (Testing)</option>
                      <option value="production">Production (Live)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm">IPN ID</Label>
                    <Input 
                      value={pesapal.ipn_id || ''} 
                      disabled
                      placeholder="Auto-generated on registration" 
                      className="mt-1 bg-slate-50" 
                    />
                  </div>
                </div>
                {pesapalTestResult && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${pesapalTestResult.success ? 'bg-emerald-50 text-emerald-700' : pesapalTestResult.loading ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
                    {pesapalTestResult.loading ? (
                      <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {pesapalTestResult.message}</span>
                    ) : (
                      <span className="flex items-center gap-2">
                        {pesapalTestResult.success ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        {pesapalTestResult.message}
                      </span>
                    )}
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleSavePesapal} disabled={pesapalSaving} className="bg-slate-800 hover:bg-slate-900 text-white rounded-xl" data-testid="save-pesapal-btn">
                    {pesapalSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Pesapal Config
                  </Button>
                  <Button onClick={handleTestPesapal} variant="outline" disabled={!pesapal.consumer_key} className="rounded-xl" data-testid="test-pesapal-btn">
                    Test Connection
                  </Button>
                </div>
              </div>

              {/* Recent Transactions */}
              {transactions.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200/60 p-5">
                  <h3 className="text-sm font-bold text-slate-700 mb-3">Recent Transactions</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-slate-100">
                        <th className="text-left py-2 text-[10px] font-semibold text-slate-400 uppercase">ID</th>
                        <th className="text-left py-2 text-[10px] font-semibold text-slate-400 uppercase">Method</th>
                        <th className="text-left py-2 text-[10px] font-semibold text-slate-400 uppercase">Plan</th>
                        <th className="text-right py-2 text-[10px] font-semibold text-slate-400 uppercase">Amount</th>
                        <th className="text-center py-2 text-[10px] font-semibold text-slate-400 uppercase">Status</th>
                        <th className="text-right py-2 text-[10px] font-semibold text-slate-400 uppercase">Date</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-50">
                        {transactions.slice(0, 20).map(t => (
                          <tr key={t.transaction_id}>
                            <td className="py-2 text-xs text-slate-500">{t.transaction_id}</td>
                            <td className="py-2 capitalize text-xs">{t.payment_method}</td>
                            <td className="py-2 capitalize text-xs">{t.plan_code}</td>
                            <td className="py-2 text-right text-xs font-medium">{t.currency} {t.amount?.toLocaleString()}</td>
                            <td className="py-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${t.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : t.payment_status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{t.payment_status}</span></td>
                            <td className="py-2 text-right text-[10px] text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* ─── CHANNEL PARTNERS ─── */}
          {section === 'channels' && (
            <div className="space-y-4" data-testid="platform-channels">
              <h2 className="text-xl font-bold text-slate-800">Channel Partners</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {partners.map(p => (
                  <div key={p.partner_id} className="bg-white rounded-2xl border border-slate-200/60 p-5" data-testid={`partner-${p.code}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-slate-800">{p.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        p.status === 'active' ? 'bg-emerald-100 text-emerald-700' : p.status === 'planned' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                      }`}>{p.status}</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">{p.description}</p>
                    {p.documentation_url && (
                      <a href={p.documentation_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                        <Globe className="w-3 h-3" /> Documentation
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── MOBILE APP ─── */}
          {section === 'mobile-app' && (
            <div className="space-y-6" data-testid="platform-mobile-app">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Mobile Apps</h2>
                <p className="text-sm text-slate-500 mt-1">Download and manage mobile applications for hosts and guests</p>
              </div>

              {/* Host App Banner */}
              <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Smartphone className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-white/70 uppercase tracking-wide">For Property Hosts</p>
                    <h3 className="text-xl font-bold">iHostPMS Mobile</h3>
                    <p className="text-white/80 text-sm">Version 1.0.0 - React Native</p>
                  </div>
                </div>
              </div>

              {/* Host App Downloads */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">Android (Host)</h4>
                      <p className="text-xs text-slate-500">APK Download</p>
                    </div>
                  </div>
                  <a href="https://expo.dev/artifacts/eas/creVoYwAHUoKgMF3a1WFy7.apk" target="_blank" rel="noopener noreferrer" className="block"
                    onClick={() => { try { api('/track-download', { method: 'POST', body: JSON.stringify({ app_type: 'host' }) }); } catch {} }}>
                    <Button className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl" data-testid="host-android-download">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Download Host APK
                    </Button>
                  </a>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-500 font-medium mb-2">Requirements:</p>
                    <ul className="text-xs text-slate-500 space-y-1">
                      <li>- Android 8.0 (Oreo) or later</li>
                      <li>- ~50MB storage space</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">iOS (Host)</h4>
                      <p className="text-xs text-slate-500">TestFlight Beta</p>
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm text-amber-800 font-medium mb-1">Coming Soon</p>
                    <p className="text-xs text-amber-600">iOS TestFlight build requires Apple Developer credentials.</p>
                  </div>
                </div>
              </div>

              {/* Guest App Banner */}
              <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Users className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-white/70 uppercase tracking-wide">For Guests</p>
                    <h3 className="text-xl font-bold">iStayBnB</h3>
                    <p className="text-white/80 text-sm">Version 1.0.0 - React Native - M-Pesa & Pesapal</p>
                  </div>
                </div>
              </div>

              {/* Guest App Downloads */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">Android (Guest)</h4>
                      <p className="text-xs text-slate-500">iStayBnB APK</p>
                    </div>
                  </div>
                  <a href="https://expo.dev/artifacts/eas/mwkRuexrysrgRydZ6srQJ5.apk" target="_blank" rel="noopener noreferrer" className="block"
                    onClick={() => { try { api('/track-download', { method: 'POST', body: JSON.stringify({ app_type: 'guest' }) }); } catch {} }}>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="guest-android-download">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      Download iStayBnB APK
                    </Button>
                  </a>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-500 font-medium mb-2">Requirements:</p>
                    <ul className="text-xs text-slate-500 space-y-1">
                      <li>- Android 8.0 (Oreo) or later</li>
                      <li>- ~50MB storage space</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                      <svg className="w-6 h-6 text-slate-800" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800">iOS (Guest)</h4>
                      <p className="text-xs text-slate-500">TestFlight Beta</p>
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm text-amber-800 font-medium mb-1">Coming Soon</p>
                    <p className="text-xs text-amber-600">iOS TestFlight build requires Apple Developer credentials.</p>
                  </div>
                </div>
              </div>

              {/* Installation Guide */}
              <div className="bg-white rounded-2xl border border-slate-200/60 p-6">
                <h4 className="font-bold text-slate-800 mb-4">Android Installation Guide</h4>
                <ol className="space-y-3">
                  {[
                    'Click the "Download APK" button for the desired app above',
                    'Open the downloaded file on your Android device',
                    'If prompted, allow installation from unknown sources',
                    'Follow the on-screen instructions to complete installation',
                    'Open the app and log in with your account'
                  ].map((step, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-sm text-slate-600">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Technical Info */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <h4 className="font-bold text-slate-800 mb-4">Technical Information</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Framework</p>
                    <p className="text-sm font-medium text-slate-800">React Native (Expo)</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Build System</p>
                    <p className="text-sm font-medium text-slate-800">EAS Build</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Auth Storage</p>
                    <p className="text-sm font-medium text-slate-800">SecureStore (Encrypted)</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Distribution</p>
                    <p className="text-sm font-medium text-slate-800">Internal Testing</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── USERS & SECURITY (MERGED) ─── */}
          {section === 'users-security' && <UsersSecuritySection api={api} />}

          {/* ─── CONTENT MODERATION (includes Testimonials) ─── */}
          {section === 'moderation' && (
            <div className="space-y-6" data-testid="platform-moderation">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Content Moderation</h2>
                <p className="text-sm text-slate-500 mt-1">Review and moderate guest reviews and testimonials across all tenants</p>
              </div>

              {/* Tabs */}
              <div className="flex gap-2">
                <Button
                  variant={moderationTab === 'reviews' ? 'default' : 'outline'}
                  onClick={() => setModerationTab('reviews')}
                  className={moderationTab === 'reviews' ? 'bg-slate-800' : ''}
                >
                  <MessageSquare className="w-4 h-4 mr-2" /> Guest Reviews
                </Button>
                <Button
                  variant={moderationTab === 'testimonials' ? 'default' : 'outline'}
                  onClick={() => setModerationTab('testimonials')}
                  className={moderationTab === 'testimonials' ? 'bg-slate-800' : ''}
                >
                  <Star className="w-4 h-4 mr-2" /> Testimonials ({testimonialCounts.total || 0})
                </Button>
              </div>

              {/* Spam Stats */}
              {moderationTab === 'reviews' && spamStats && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200">
                    <p className="text-lg font-bold text-emerald-600">{spamStats.high_quality || 0}</p>
                    <p className="text-xs text-emerald-700">High Quality</p>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                    <p className="text-lg font-bold text-blue-600">{spamStats.good || 0}</p>
                    <p className="text-xs text-blue-700">Good</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
                    <p className="text-lg font-bold text-amber-600">{spamStats.needs_review || 0}</p>
                    <p className="text-xs text-amber-700">Needs Review</p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-3 border border-orange-200">
                    <p className="text-lg font-bold text-orange-600">{spamStats.suspicious || 0}</p>
                    <p className="text-xs text-orange-700">Suspicious</p>
                  </div>
                  <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                    <p className="text-lg font-bold text-red-600">{spamStats.likely_spam || 0}</p>
                    <p className="text-xs text-red-700">Likely Spam</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                    <p className="text-lg font-bold text-slate-600">{spamStats.flagged_count || 0}</p>
                    <p className="text-xs text-slate-700">Flagged</p>
                  </div>
                </div>
              )}

              {/* Search & Filters */}
              <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search by guest name, property, or content..."
                    value={moderationSearch}
                    onChange={(e) => setModerationSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <select
                  value={moderationFilter}
                  onChange={(e) => setModerationFilter(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="flagged">Flagged</option>
                  <option value="spam">Likely Spam</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="hidden">Hidden</option>
                </select>
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    setScanningSpam(true);
                    try {
                      const token = localStorage.getItem('ihostpms_admin_token');
                      await fetch(`${BACKEND_URL}/api/admin/moderation/reviews/scan-spam`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      // Reload data after scan
                      loadSection('moderation');
                      // Load spam stats
                      const statsResp = await fetch(`${BACKEND_URL}/api/admin/moderation/reviews/spam-stats`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (statsResp.ok) setSpamStats(await statsResp.json());
                    } finally {
                      setScanningSpam(false);
                    }
                  }}
                  disabled={scanningSpam}
                  className="bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                >
                  {scanningSpam ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                  {scanningSpam ? 'Scanning...' : 'Scan for Spam'}
                </Button>
                <Button variant="outline" onClick={() => loadSection('moderation')}>
                  <Activity className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>

              {/* Bulk Actions */}
              {moderationTab === 'reviews' && selectedReviews.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-slate-100 rounded-xl border border-slate-200">
                  <span className="text-sm font-medium text-slate-700">
                    {selectedReviews.length} selected
                  </span>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    disabled={bulkActionLoading}
                    onClick={async () => {
                      setBulkActionLoading(true);
                      try {
                        const token = localStorage.getItem('ihostpms_admin_token');
                        await fetch(`${BACKEND_URL}/api/admin/moderation/reviews/bulk`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ review_ids: selectedReviews, action: 'flag' })
                        });
                        setSelectedReviews([]);
                        loadSection('moderation');
                      } finally {
                        setBulkActionLoading(false);
                      }
                    }}
                  >
                    <Flag className="w-3 h-3 mr-1" /> Flag All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-slate-600 hover:bg-slate-50"
                    disabled={bulkActionLoading}
                    onClick={async () => {
                      setBulkActionLoading(true);
                      try {
                        const token = localStorage.getItem('ihostpms_admin_token');
                        await fetch(`${BACKEND_URL}/api/admin/moderation/reviews/bulk`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ review_ids: selectedReviews, action: 'hide' })
                        });
                        setSelectedReviews([]);
                        loadSection('moderation');
                      } finally {
                        setBulkActionLoading(false);
                      }
                    }}
                  >
                    <Ban className="w-3 h-3 mr-1" /> Hide All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    disabled={bulkActionLoading}
                    onClick={async () => {
                      if (!window.confirm(`Delete ${selectedReviews.length} reviews? This cannot be undone.`)) return;
                      setBulkActionLoading(true);
                      try {
                        const token = localStorage.getItem('ihostpms_admin_token');
                        await fetch(`${BACKEND_URL}/api/admin/moderation/reviews/bulk`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ review_ids: selectedReviews, action: 'delete' })
                        });
                        setSelectedReviews([]);
                        loadSection('moderation');
                      } finally {
                        setBulkActionLoading(false);
                      }
                    }}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete All
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedReviews([])}
                  >
                    <X className="w-3 h-3 mr-1" /> Clear
                  </Button>
                </div>
              )}

              {/* Reviews Moderation */}
              {moderationTab === 'reviews' && (
                <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
                  <div className="p-4 border-b border-slate-100 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-slate-300"
                          checked={selectedReviews.length > 0 && selectedReviews.length === allReviews.filter(r => {
                            if (moderationFilter === 'flagged') return r.flagged;
                            if (moderationFilter === 'spam') return r.spam_score >= 50;
                            if (moderationFilter !== 'all') return r.status === moderationFilter;
                            return true;
                          }).length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const filteredIds = allReviews
                                .filter(r => {
                                  if (moderationFilter === 'flagged') return r.flagged;
                                  if (moderationFilter === 'spam') return r.spam_score >= 50;
                                  if (moderationFilter !== 'all') return r.status === moderationFilter;
                                  return true;
                                })
                                .map(r => r.review_id);
                              setSelectedReviews(filteredIds);
                            } else {
                              setSelectedReviews([]);
                            }
                          }}
                        />
                        <Flag className="w-5 h-5 text-red-500" />
                        <h3 className="font-semibold text-slate-800">Guest Reviews Moderation</h3>
                        <span className="text-sm text-slate-500 ml-2">({allReviews.length} total)</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">Remove inappropriate, fake, or malicious reviews from any tenant property</p>
                  </div>

                  {allReviews.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                      <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p>No reviews to moderate</p>
                      <p className="text-xs mt-1">Reviews will appear here when guests submit feedback</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {allReviews
                        .filter(r => {
                          if (moderationFilter === 'flagged') return r.flagged;
                          if (moderationFilter === 'spam') return r.spam_score >= 50;
                          if (moderationFilter !== 'all') return r.status === moderationFilter;
                          return true;
                        })
                        .filter(r => {
                          if (!moderationSearch) return true;
                          const search = moderationSearch.toLowerCase();
                          return (
                            r.guest_name?.toLowerCase().includes(search) ||
                            r.content?.toLowerCase().includes(search) ||
                            r.property_name?.toLowerCase().includes(search)
                          );
                        })
                        .map(review => (
                          <div key={review.review_id} className={`p-4 ${review.flagged ? 'bg-red-50' : review.spam_score >= 50 ? 'bg-amber-50' : ''}`} data-testid={`mod-review-${review.review_id}`}>
                            <div className="flex items-start gap-3">
                              {/* Checkbox */}
                              <input
                                type="checkbox"
                                className="w-4 h-4 mt-1 rounded border-slate-300"
                                checked={selectedReviews.includes(review.review_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedReviews(prev => [...prev, review.review_id]);
                                  } else {
                                    setSelectedReviews(prev => prev.filter(id => id !== review.review_id));
                                  }
                                }}
                              />
                              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                                <span className="text-slate-600 font-medium">{review.guest_name?.charAt(0) || 'G'}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <div>
                                    <p className="font-semibold text-slate-800">{review.guest_name}</p>
                                    <p className="text-sm text-slate-500">{review.property_name} · {review.tenant_name || 'Unknown Tenant'}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {review.flagged && (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                        <Flag className="w-3 h-3 inline mr-1" /> Flagged
                                      </span>
                                    )}
                                    {review.spam_score >= 50 && (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700" title={review.spam_flags?.join(', ')}>
                                        <Shield className="w-3 h-3 inline mr-1" /> Spam: {review.spam_score}%
                                      </span>
                                    )}
                                    {review.spam_score > 0 && review.spam_score < 50 && (
                                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600" title={review.spam_flags?.join(', ')}>
                                        Score: {review.spam_score}
                                      </span>
                                    )}
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      review.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                      review.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                      review.status === 'hidden' ? 'bg-slate-200 text-slate-600' :
                                      'bg-slate-100 text-slate-700'
                                    }`}>
                                      {review.status}
                                    </span>
                                  </div>
                                </div>

                                {/* Rating */}
                                <div className="flex items-center gap-1 mb-2">
                                  {[...Array(5)].map((_, i) => (
                                    <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                                  ))}
                                  <span className="text-sm text-slate-500 ml-2">{review.source && `via ${review.source}`}</span>
                                </div>

                                {review.title && <p className="font-medium text-slate-700 mb-1">{review.title}</p>}
                                <p className="text-slate-600 text-sm">{review.content}</p>

                                {/* Spam Details */}
                                {review.spam_flags && review.spam_flags.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {review.spam_flags.map((flag, idx) => (
                                      <span key={idx} className="px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-700 font-medium">
                                        {flag.replace(/_/g, ' ')}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                <p className="text-xs text-slate-400 mt-2">
                                  {review.created_at?.slice(0, 10)} · ID: {review.review_id}
                                </p>

                                {/* Moderation Actions */}
                                <div className="flex items-center gap-2 mt-3">
                                  {!review.flagged && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-amber-600 border-amber-200 hover:bg-amber-50 h-7"
                                      onClick={async () => {
                                        const token = localStorage.getItem('ihostpms_admin_token');
                                        await fetch(`${BACKEND_URL}/api/admin/moderation/reviews/${review.review_id}/flag`, {
                                          method: 'PUT',
                                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ reason: 'Flagged by admin for review' })
                                        });
                                        loadSection('moderation');
                                      }}
                                    >
                                      <Flag className="w-3 h-3 mr-1" /> Flag
                                    </Button>
                                  )}
                                  {review.flagged && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-7"
                                      onClick={async () => {
                                        const token = localStorage.getItem('ihostpms_admin_token');
                                        await fetch(`${BACKEND_URL}/api/admin/moderation/reviews/${review.review_id}/unflag`, {
                                          method: 'PUT',
                                          headers: { 'Authorization': `Bearer ${token}` }
                                        });
                                        loadSection('moderation');
                                      }}
                                    >
                                      <Check className="w-3 h-3 mr-1" /> Unflag
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-slate-600 hover:bg-slate-50 h-7"
                                    onClick={async () => {
                                      const token = localStorage.getItem('ihostpms_admin_token');
                                      await fetch(`${BACKEND_URL}/api/admin/moderation/reviews/${review.review_id}/hide`, {
                                        method: 'PUT',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      loadSection('moderation');
                                    }}
                                  >
                                    <Ban className="w-3 h-3 mr-1" /> Hide
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7"
                                    onClick={async () => {
                                      if (!window.confirm('Permanently delete this review? This action cannot be undone.')) return;
                                      const token = localStorage.getItem('ihostpms_admin_token');
                                      await fetch(`${BACKEND_URL}/api/admin/moderation/reviews/${review.review_id}`, {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      loadSection('moderation');
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" /> Delete
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {/* Testimonials Tab - Shows same as testimonials section */}
              {moderationTab === 'testimonials' && (
                <div className="space-y-4">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className={`p-4 rounded-xl border cursor-pointer transition-colors ${testimonialFilter === 'pending' ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200 hover:border-amber-200'}`} onClick={() => setTestimonialFilter(f => f === 'pending' ? 'all' : 'pending')}>
                      <p className="text-2xl font-bold text-amber-600">{testimonialCounts.pending}</p>
                      <p className="text-sm text-slate-500">Pending</p>
                    </div>
                    <div className={`p-4 rounded-xl border cursor-pointer transition-colors ${testimonialFilter === 'approved' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200 hover:border-emerald-200'}`} onClick={() => setTestimonialFilter(f => f === 'approved' ? 'all' : 'approved')}>
                      <p className="text-2xl font-bold text-emerald-600">{testimonialCounts.approved}</p>
                      <p className="text-sm text-slate-500">Approved</p>
                    </div>
                    <div className={`p-4 rounded-xl border cursor-pointer transition-colors ${testimonialFilter === 'featured' ? 'bg-purple-50 border-purple-200' : 'bg-white border-slate-200 hover:border-purple-200'}`} onClick={() => setTestimonialFilter(f => f === 'featured' ? 'all' : 'featured')}>
                      <p className="text-2xl font-bold text-purple-600">{testimonialCounts.featured}</p>
                      <p className="text-sm text-slate-500">Featured</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white border border-slate-200">
                      <p className="text-2xl font-bold text-slate-600">{testimonialCounts.total}</p>
                      <p className="text-sm text-slate-500">Total</p>
                    </div>
                  </div>

                  {/* Testimonials List */}
                  <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-amber-500" />
                        <h3 className="font-semibold text-slate-800">
                          {testimonialFilter === 'all' ? 'All Testimonials' : `${testimonialFilter.charAt(0).toUpperCase() + testimonialFilter.slice(1)} Testimonials`}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        {testimonialFilter !== 'all' && (
                          <Button variant="ghost" size="sm" onClick={() => setTestimonialFilter('all')}>
                            Clear Filter
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => loadSection('moderation')}>
                          <Activity className="w-3.5 h-3.5 mr-1" /> Refresh
                        </Button>
                      </div>
                    </div>

                    {testimonials.filter(t => testimonialFilter === 'all' || t.status === testimonialFilter).length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        No testimonials {testimonialFilter !== 'all' ? `with status "${testimonialFilter}"` : 'yet'}
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {testimonials
                          .filter(t => testimonialFilter === 'all' || t.status === testimonialFilter)
                          .map(testimonial => (
                            <div key={testimonial.testimonial_id} className="p-4" data-testid={`testimonial-${testimonial.testimonial_id}`}>
                              <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF385C] to-[#E31C5F] flex items-center justify-center flex-shrink-0">
                                  <span className="text-white font-semibold text-lg">{testimonial.author_name?.charAt(0) || 'H'}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <div>
                                      <p className="font-semibold text-slate-800">{testimonial.author_name}</p>
                                      <p className="text-sm text-slate-500">{testimonial.role} {testimonial.company_name && `· ${testimonial.company_name}`}</p>
                                    </div>
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      testimonial.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                      testimonial.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                                      testimonial.status === 'featured' ? 'bg-purple-100 text-purple-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {testimonial.status}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1 mb-2">
                                    {[...Array(5)].map((_, i) => (
                                      <Star key={i} className={`w-4 h-4 ${i < testimonial.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                                    ))}
                                  </div>
                                  <p className="text-slate-600 text-sm">{testimonial.content}</p>
                                  <p className="text-xs text-slate-400 mt-2">
                                    Submitted {testimonial.created_at?.slice(0, 10)} · {testimonial.author_email}
                                  </p>
                                  <div className="flex items-center gap-2 mt-3">
                                    {testimonial.status === 'pending' && (
                                      <>
                                        <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white h-7"
                                          onClick={async () => {
                                            const token = localStorage.getItem('ihostpms_admin_token');
                                            await fetch(`${BACKEND_URL}/api/testimonials/admin/${testimonial.testimonial_id}/approve`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }});
                                            loadSection('moderation');
                                          }}>
                                          <Check className="w-3 h-3 mr-1" /> Approve
                                        </Button>
                                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-7"
                                          onClick={async () => {
                                            const token = localStorage.getItem('ihostpms_admin_token');
                                            await fetch(`${BACKEND_URL}/api/testimonials/admin/${testimonial.testimonial_id}/reject`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }});
                                            loadSection('moderation');
                                          }}>
                                          <X className="w-3 h-3 mr-1" /> Reject
                                        </Button>
                                      </>
                                    )}
                                    {testimonial.status === 'approved' && (
                                      <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white h-7"
                                        onClick={async () => {
                                          const token = localStorage.getItem('ihostpms_admin_token');
                                          await fetch(`${BACKEND_URL}/api/testimonials/admin/${testimonial.testimonial_id}/feature?order=${testimonialCounts.featured + 1}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }});
                                          loadSection('moderation');
                                        }}>
                                        <Star className="w-3 h-3 mr-1" /> Feature
                                      </Button>
                                    )}
                                    {testimonial.status === 'featured' && (
                                      <Button size="sm" variant="outline" className="h-7"
                                        onClick={async () => {
                                          const token = localStorage.getItem('ihostpms_admin_token');
                                          await fetch(`${BACKEND_URL}/api/testimonials/admin/${testimonial.testimonial_id}/approve`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }});
                                          loadSection('moderation');
                                        }}>
                                        Unfeature
                                      </Button>
                                    )}
                                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50 h-7"
                                      onClick={async () => {
                                        if (!window.confirm('Delete this testimonial?')) return;
                                        const token = localStorage.getItem('ihostpms_admin_token');
                                        await fetch(`${BACKEND_URL}/api/testimonials/admin/${testimonial.testimonial_id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }});
                                        loadSection('moderation');
                                      }}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Moderation Guidelines */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Moderation Guidelines</p>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1">
                      <li>• <strong>Flag:</strong> Mark suspicious content for further review</li>
                      <li>• <strong>Hide:</strong> Remove from public display but keep in database</li>
                      <li>• <strong>Delete:</strong> Permanently remove malicious or spam content</li>
                      <li>• Consider the context before taking action - genuine negative feedback is valuable</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── SMTP ─── */}
          {/* ─── WEBSITE SETTINGS (merged SMTP + Website Config) ─── */}
          {section === 'website-settings' && <WebsiteSettingsSection api={api} />}

          {section === 'faqs' && (
            <div className="space-y-4" data-testid="platform-faqs">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">FAQs & Documentation</h2>
                <Button onClick={() => { setFaqForm({ question: '', answer: '', category: 'general', sort_order: 0 }); setFaqDialog('new'); }} className="bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-sm" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Add FAQ
                </Button>
              </div>
              <div className="space-y-3">
                {faqs.map(f => (
                  <div key={f.faq_id} className="bg-white rounded-2xl border border-slate-200/60 p-5" data-testid={`faq-${f.faq_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <span className="text-[10px] font-semibold uppercase text-slate-400 bg-slate-50 px-2 py-0.5 rounded">{f.category}</span>
                        <h4 className="text-sm font-bold text-slate-800 mt-2">{f.question}</h4>
                        <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap">{f.answer}</p>
                      </div>
                      <div className="flex gap-1 ml-3 shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setFaqForm(f); setFaqDialog(f.faq_id); }}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDeleteFAQ(f.faq_id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── ERROR LOGS ─── */}
          {section === 'errors' && (
            <div className="space-y-4" data-testid="platform-errors">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">Error Logs</h2>
                <Button onClick={async () => { await api('/error-logs/clear', { method: 'POST' }); loadSection('errors'); }} variant="outline" size="sm" className="rounded-xl text-xs">Clear Logs</Button>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
                {errors.length === 0 ? (
                  <div className="text-center py-12 text-slate-400"><FileWarning className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No error logs</p></div>
                ) : (
                  <div className="divide-y divide-slate-50 max-h-96 overflow-y-auto">
                    {errors.map((e, i) => (
                      <div key={i} className="px-5 py-3">
                        <div className="flex items-center gap-2 text-xs text-red-500 font-medium">{e.level || 'ERROR'} <span className="text-slate-400">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</span></div>
                        <p className="text-sm text-slate-700 mt-1">{e.message}</p>
                        {e.source && <p className="text-[10px] text-slate-400 mt-0.5">{e.source}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── DEPLOYMENT GUIDE ─── */}
          {section === 'deployment' && (
            <div className="space-y-6" data-testid="platform-deployment-guide">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">Production Deployment Guide</h2>
                <a 
                  href="/DEPLOYMENT_GUIDE.md" 
                  target="_blank"
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <BookOpen className="w-4 h-4" />
                  View Full Guide
                </a>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <h3 className="font-semibold text-blue-900 mb-2">Multi-Domain Architecture</h3>
                <p className="text-sm text-blue-700">
                  iHostPMS and iStayBnB can be deployed on separate domains (e.g., ihostpms.com and istaybnb.com) 
                  while sharing the same MongoDB database and FastAPI backend.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-slate-200/60 p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-rose-500 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">iHostPMS</h4>
                      <p className="text-xs text-slate-500">Property Management Platform</p>
                    </div>
                  </div>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Host dashboard & property management</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Booking & calendar management</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Channel manager & OTA sync</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Host mobile app (React Native)</li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-2xl border border-slate-200/60 p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">iStayBnB</h4>
                      <p className="text-xs text-slate-500">Guest Marketplace</p>
                    </div>
                  </div>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Property search & discovery</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Guest booking & payments</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> M-Pesa & Pesapal integration</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Guest mobile app (React Native)</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
                <div className="px-5 py-4 bg-slate-50 border-b border-slate-200/60">
                  <h3 className="font-semibold text-slate-800">Server Requirements</h3>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="font-semibold text-slate-800">OS</p>
                      <p className="text-slate-500 text-xs mt-1">Ubuntu 22.04 LTS</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="font-semibold text-slate-800">RAM</p>
                      <p className="text-slate-500 text-xs mt-1">4GB minimum</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="font-semibold text-slate-800">Storage</p>
                      <p className="text-slate-500 text-xs mt-1">50GB SSD</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-xl">
                      <p className="font-semibold text-slate-800">Database</p>
                      <p className="text-slate-500 text-xs mt-1">MongoDB 6.0+</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
                <div className="px-5 py-4 bg-slate-50 border-b border-slate-200/60">
                  <h3 className="font-semibold text-slate-800">Quick Start Checklist</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { step: '1', title: 'Install Dependencies', desc: 'Node.js 18+, Python 3.11+, MongoDB, Nginx' },
                    { step: '2', title: 'Clone Repository', desc: 'Set up /var/www/ihostpms and /var/www/istaybnb' },
                    { step: '3', title: 'Configure Environment', desc: 'Set up .env files for backend and frontend' },
                    { step: '4', title: 'Setup Nginx', desc: 'Configure reverse proxy for both domains' },
                    { step: '5', title: 'SSL Certificates', desc: 'Install Let\'s Encrypt certificates with Certbot' },
                    { step: '6', title: 'Start Services', desc: 'Use PM2 to manage backend process' },
                    { step: '7', title: 'Build Mobile Apps', desc: 'Run EAS build for Android/iOS APKs' },
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{item.title}</p>
                        <p className="text-xs text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-800">Important Notes</h4>
                    <ul className="text-sm text-amber-700 mt-2 space-y-1">
                      <li>• Both platforms share the same MongoDB database</li>
                      <li>• Hosts and Guests use separate authentication (users vs guests collections)</li>
                      <li>• Configure CORS properly for cross-domain API access</li>
                      <li>• Set up backup strategy before going live</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── SUPPORT SESSIONS ─── */}
          {section === 'support' && (
            <div className="space-y-4" data-testid="platform-support-sessions">
              <h2 className="text-xl font-bold text-slate-800">Support Sessions</h2>
              <p className="text-sm text-slate-500">Track admin access to tenant accounts for support purposes. All actions are logged for audit.</p>
              <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
                {supportSessions.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <UserPlus className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No support sessions</p>
                    <p className="text-xs mt-1">Use "Login As" from the Tenants page to start a support session</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-50 max-h-[500px] overflow-y-auto">
                    {supportSessions.map((s) => (
                      <div key={s.session_id} className="px-5 py-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-800">{s.tenant_name}</p>
                            <p className="text-xs text-slate-400">by {s.admin_email}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            s.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {s.status}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-slate-500">
                          <p><span className="text-slate-400">Reason:</span> {s.reason}</p>
                          <p><span className="text-slate-400">Started:</span> {new Date(s.started_at).toLocaleString()}</p>
                          {s.ended_at && <p><span className="text-slate-400">Ended:</span> {new Date(s.ended_at).toLocaleString()}</p>}
                        </div>
                        {s.actions?.length > 0 && (
                          <div className="mt-2 text-xs">
                            <p className="text-slate-400 mb-1">Actions ({s.actions.length}):</p>
                            <div className="bg-slate-50 rounded p-2 max-h-20 overflow-y-auto">
                              {s.actions.slice(-5).map((a, i) => (
                                <p key={i} className="text-slate-600">{a}</p>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── ANALYTICS ─── */}
          {section === 'analytics' && <AnalyticsSection api={api} />}

          {/* ─── AUDIT LOG ─── */}
          {section === 'audit' && (
            <div className="space-y-4" data-testid="platform-audit">
              <h2 className="text-xl font-bold text-slate-800">Audit Log</h2>
              <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden max-h-[500px] overflow-y-auto">
                {audit.length === 0 ? <div className="text-center py-12 text-slate-400 text-sm">No entries</div> : (
                  <div className="divide-y divide-slate-50">
                    {audit.map((log, i) => (
                      <div key={i} className="px-5 py-3 flex items-center gap-3">
                        <Shield className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="flex-1"><span className="text-sm font-medium text-slate-700 capitalize">{log.action?.replace(/_/g, ' ')}</span>
                          {log.admin_email && <span className="text-xs text-slate-400 ml-2">by {log.admin_email}</span>}
                        </div>
                        <span className="text-[10px] text-slate-400 shrink-0">{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ─── DIALOGS ─── */}
      {/* Approve/Decline Dialog */}
      <Dialog open={!!approveDialog} onOpenChange={() => setApproveDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review Registration: {approveDialog?.name}</DialogTitle>
            <DialogDescription className="sr-only">Review and approve or decline tenant registration</DialogDescription>
          </DialogHeader>
          {approveDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 text-sm space-y-1">
                <p><span className="text-slate-500">Owner:</span> <strong>{approveDialog.owner_name}</strong> ({approveDialog.owner_email})</p>
                <p><span className="text-slate-500">Registered:</span> {approveDialog.created_at ? new Date(approveDialog.created_at).toLocaleDateString() : 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Assign Plan</Label>
                <select data-testid="approval-plan" value={approvalPlan} onChange={e => setApprovalPlan(e.target.value)} className="w-full h-9 mt-1 px-3 border border-slate-200 rounded-lg text-sm">
                  {plans.map(p => !p.is_contact_us && <option key={p.code} value={p.code}>{p.name} - KES {p.price?.toLocaleString()}/mo</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm font-medium">Trial Period</Label>
                <select data-testid="approval-trial" value={approvalTrialDays} onChange={e => setApprovalTrialDays(Number(e.target.value))} className="w-full h-9 mt-1 px-3 border border-slate-200 rounded-lg text-sm">
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={21}>21 days</option>
                </select>
              </div>
              <div>
                <Label className="text-sm font-medium">Notes (optional)</Label>
                <Input data-testid="approval-notes" value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)} placeholder="Internal notes..." className="mt-1" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={() => handleApproveDecline('approve')} disabled={processing} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="approve-btn">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />} Approve
                </Button>
                <Button onClick={() => handleApproveDecline('decline')} disabled={processing} variant="outline" className="flex-1 rounded-xl text-red-600 border-red-200 hover:bg-red-50" data-testid="decline-btn">
                  <X className="w-4 h-4 mr-2" /> Decline
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tenant Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Tenant Details</DialogTitle><DialogDescription className="sr-only">View tenant details</DialogDescription></DialogHeader>
          {detailDialog && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-slate-400">Name:</span> <strong>{detailDialog.name}</strong></div>
                <div><span className="text-slate-400">Status:</span> {statusBadge(detailDialog.status)}</div>
                <div><span className="text-slate-400">Plan:</span> <strong className="capitalize">{detailDialog.plan}</strong></div>
                <div><span className="text-slate-400">Bookings:</span> <strong>{detailDialog.booking_count}</strong></div>
                <div><span className="text-slate-400">Revenue:</span> <strong>KES {detailDialog.total_revenue?.toLocaleString()}</strong></div>
                {detailDialog.trial_end && <div><span className="text-slate-400">Trial Ends:</span> <strong>{new Date(detailDialog.trial_end).toLocaleDateString()}</strong></div>}
              </div>
              
              {/* Show decline reason for declined tenants */}
              {detailDialog.status === 'declined' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <h4 className="font-bold text-red-700 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Decline Information
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-red-600">Reason:</span> <strong>{detailDialog.decline_reason || 'No reason provided'}</strong></p>
                    {detailDialog.declined_at && (
                      <p><span className="text-red-600">Declined:</span> <strong>{new Date(detailDialog.declined_at).toLocaleString()}</strong></p>
                    )}
                    {detailDialog.declined_by && (
                      <p><span className="text-red-600">By:</span> <strong>{detailDialog.declined_by}</strong></p>
                    )}
                  </div>
                </div>
              )}
              
              <div>
                <h4 className="font-bold text-slate-700 mb-2">Users ({detailDialog.users?.length || 0})</h4>
                {detailDialog.users?.map(u => (
                  <div key={u.user_id} className="flex justify-between py-1"><span>{u.name} ({u.email})</span><span className="text-xs text-slate-400 capitalize">{u.role}</span></div>
                ))}
                {(!detailDialog.users || detailDialog.users.length === 0) && <p className="text-slate-400">No users</p>}
              </div>
              <div>
                <h4 className="font-bold text-slate-700 mb-2">Properties ({detailDialog.properties?.length || 0})</h4>
                {detailDialog.properties?.map(p => (
                  <div key={p.property_id} className="flex justify-between py-1"><span>{p.name}</span><span className="text-xs text-slate-500">{p.city}</span></div>
                ))}
                {(!detailDialog.properties || detailDialog.properties.length === 0) && <p className="text-slate-400">No properties</p>}
              </div>
              
              {/* Delete button for declined tenants */}
              {detailDialog.status === 'declined' && (
                <div className="pt-4 border-t border-slate-200">
                  <Button 
                    onClick={async () => {
                      await handleDeleteTenant(detailDialog.tenant_id);
                      setDetailDialog(null);
                    }}
                    disabled={processing}
                    variant="outline" 
                    className="w-full rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="delete-declined-tenant"
                  >
                    {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Delete This Tenant
                  </Button>
                </div>
              )}

              {/* Trial Extension for active/expired tenants */}
              {detailDialog.status !== 'declined' && (
                <div className="pt-4 border-t border-slate-200">
                  <h4 className="font-bold text-slate-700 mb-2">Extend Trial</h4>
                  <div className="flex gap-2">
                    {[7, 14, 30].map(days => (
                      <Button
                        key={days}
                        size="sm"
                        variant="outline"
                        className="rounded-lg text-xs"
                        data-testid={`extend-trial-${days}`}
                        onClick={async () => {
                          try {
                            const res = await api(`/tenants/${detailDialog.tenant_id}/extend-trial`, { method: 'POST', body: JSON.stringify({ additional_days: days }) });
                            const data = typeof res === 'string' ? JSON.parse(res) : res;
                            alert(data.message || `Trial extended by ${days} days`);
                            setDetailDialog(null);
                            loadSection('tenants');
                          } catch (err) { alert(err.message || 'Failed to extend trial'); }
                        }}
                      >
                        +{days} days
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite Team Member Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Invite Team Member</DialogTitle><DialogDescription>An invitation email with temporary login credentials will be sent.</DialogDescription></DialogHeader>
          <form onSubmit={handleInviteMember} className="space-y-4">
            <div><Label className="text-sm">Name</Label><Input value={newMember.name} onChange={e => setNewMember(m => ({...m, name: e.target.value}))} required className="mt-1" /></div>
            <div><Label className="text-sm">Email</Label><Input type="email" value={newMember.email} onChange={e => setNewMember(m => ({...m, email: e.target.value}))} required className="mt-1" /></div>
            <div><Label className="text-sm">Role</Label>
              <select value={newMember.role} onChange={e => setNewMember(m => ({...m, role: e.target.value}))} className="w-full h-9 mt-1 px-3 border rounded-lg text-sm">
                <option value="admin">Admin - Full platform access</option><option value="support">Support - View and assist tenants</option><option value="viewer">Viewer - Read-only access</option>
              </select>
            </div>
            <Button type="submit" disabled={processing} className="w-full bg-slate-800 hover:bg-slate-900 text-white rounded-xl">
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />} Send Invitation
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* FAQ Edit Dialog */}
      <Dialog open={!!faqDialog} onOpenChange={() => setFaqDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{faqDialog === 'new' ? 'Add FAQ' : 'Edit FAQ'}</DialogTitle><DialogDescription className="sr-only">Manage FAQ entry</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Category</Label>
              <select value={faqForm.category} onChange={e => setFaqForm(f => ({...f, category: e.target.value}))} className="w-full h-9 mt-1 px-3 border rounded-lg text-sm">
                <option value="general">General</option><option value="getting_started">Getting Started</option><option value="calendar">Calendar</option>
                <option value="messaging">Messaging</option><option value="billing">Billing</option><option value="team">Team</option><option value="channels">Channels</option>
              </select>
            </div>
            <div><Label className="text-sm">Question</Label><Input value={faqForm.question} onChange={e => setFaqForm(f => ({...f, question: e.target.value}))} className="mt-1" /></div>
            <div><Label className="text-sm">Answer</Label><textarea value={faqForm.answer} onChange={e => setFaqForm(f => ({...f, answer: e.target.value}))} className="w-full mt-1 px-3 py-2 border rounded-lg text-sm min-h-[120px]" /></div>
            <Button onClick={handleSaveFAQ} disabled={processing} className="w-full bg-slate-800 hover:bg-slate-900 text-white rounded-xl">
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={!!editTenantDialog} onOpenChange={() => setEditTenantDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Tenant: {editTenantDialog?.name}</DialogTitle>
            <DialogDescription className="sr-only">Edit tenant settings</DialogDescription>
          </DialogHeader>
          {editTenantDialog && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl p-4 text-sm">
                <p><span className="text-slate-500">Tenant ID:</span> <code className="text-xs">{editTenantDialog.tenant_id}</code></p>
                <p><span className="text-slate-500">Owner:</span> {editTenantDialog.owner_name} ({editTenantDialog.owner_email})</p>
              </div>
              
              <div>
                <Label className="text-sm font-medium">Business Name</Label>
                <Input 
                  value={tenantEditForm.name} 
                  onChange={e => setTenantEditForm(f => ({...f, name: e.target.value}))} 
                  className="mt-1"
                  data-testid="edit-tenant-name"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Subscription Plan</Label>
                <select 
                  value={tenantEditForm.plan} 
                  onChange={e => setTenantEditForm(f => ({...f, plan: e.target.value}))} 
                  className="w-full h-9 mt-1 px-3 border border-slate-200 rounded-lg text-sm"
                  data-testid="edit-tenant-plan"
                >
                  {plans.map(p => <option key={p.code} value={p.code}>{p.name} {!p.is_contact_us && `- KES ${p.price?.toLocaleString()}/mo`}</option>)}
                </select>
              </div>

              <div>
                <Label className="text-sm font-medium">Account Status</Label>
                <select 
                  value={tenantEditForm.status} 
                  onChange={e => setTenantEditForm(f => ({...f, status: e.target.value}))} 
                  className="w-full h-9 mt-1 px-3 border border-slate-200 rounded-lg text-sm"
                  data-testid="edit-tenant-status"
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending</option>
                  <option value="declined">Declined</option>
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  {tenantEditForm.status === 'suspended' && '⚠️ Suspending will block user access and send notification email'}
                  {tenantEditForm.status === 'active' && '✓ User will have full access'}
                  {tenantEditForm.status === 'pending' && '⏳ User waiting for approval'}
                  {tenantEditForm.status === 'declined' && '✗ Registration was declined'}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">Admin Notes (internal)</Label>
                <textarea 
                  value={tenantEditForm.admin_notes} 
                  onChange={e => setTenantEditForm(f => ({...f, admin_notes: e.target.value}))} 
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  rows={2}
                  placeholder="Notes visible only to platform admins..."
                  data-testid="edit-tenant-notes"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditTenantDialog(null)} className="flex-1 rounded-xl">
                  Cancel
                </Button>
                <Button onClick={handleSaveTenant} disabled={processing} className="flex-1 bg-slate-800 hover:bg-slate-900 text-white rounded-xl" data-testid="save-tenant-btn">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={!!editPlanDialog} onOpenChange={() => setEditPlanDialog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Plan: {editPlanDialog?.name}</DialogTitle>
            <DialogDescription className="sr-only">Edit subscription plan</DialogDescription>
          </DialogHeader>
          {editPlanDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Plan Name</Label>
                  <Input 
                    value={planForm.name} 
                    onChange={e => setPlanForm(f => ({...f, name: e.target.value}))} 
                    className="mt-1"
                    data-testid="edit-plan-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Plan Code</Label>
                  <Input 
                    value={planForm.code} 
                    disabled
                    className="mt-1 bg-slate-50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Price (KES)</Label>
                  <Input 
                    type="number"
                    value={planForm.price} 
                    onChange={e => setPlanForm(f => ({...f, price: Number(e.target.value)}))} 
                    className="mt-1"
                    data-testid="edit-plan-price"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">Billing Cycle</Label>
                  <select 
                    value={planForm.billing_cycle} 
                    onChange={e => setPlanForm(f => ({...f, billing_cycle: e.target.value}))} 
                    className="w-full h-9 mt-1 px-3 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Max Users</Label>
                  <Input 
                    type="number"
                    value={planForm.max_users} 
                    onChange={e => setPlanForm(f => ({...f, max_users: Number(e.target.value)}))} 
                    className="mt-1"
                    data-testid="edit-plan-users"
                  />
                  <p className="text-xs text-slate-400 mt-1">Use 999 for unlimited</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Max Properties</Label>
                  <Input 
                    type="number"
                    value={planForm.max_properties} 
                    onChange={e => setPlanForm(f => ({...f, max_properties: Number(e.target.value)}))} 
                    className="mt-1"
                    data-testid="edit-plan-props"
                  />
                  <p className="text-xs text-slate-400 mt-1">Use 999 for unlimited</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Trial Days</Label>
                  <Input 
                    type="number"
                    value={planForm.trial_days} 
                    onChange={e => setPlanForm(f => ({...f, trial_days: Number(e.target.value)}))} 
                    className="mt-1"
                  />
                </div>
                <div className="flex flex-col justify-end gap-2">
                  <div className="flex items-center gap-2">
                    <Switch checked={planForm.is_active} onCheckedChange={v => setPlanForm(f => ({...f, is_active: v}))} />
                    <Label className="text-sm">Active</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={planForm.is_contact_us} onCheckedChange={v => setPlanForm(f => ({...f, is_contact_us: v}))} />
                    <Label className="text-sm">Contact Us (Hide Price)</Label>
                  </div>
                </div>
              </div>

              {/* Features Editor */}
              <div>
                <Label className="text-sm font-medium">Plan Features (Display)</Label>
                <p className="text-xs text-slate-500 mb-2">Features displayed on pricing cards and landing page</p>
                <div className="space-y-2 max-h-40 overflow-y-auto bg-slate-50 rounded-lg p-3">
                  {planForm.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      <span className="flex-1 text-sm text-slate-700">{feature}</span>
                      <button 
                        onClick={() => removeFeatureFromPlan(index)} 
                        className="text-red-400 hover:text-red-600 p-1"
                        data-testid={`remove-feature-${index}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  {planForm.features.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-2">No features added</p>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input 
                    value={newFeature} 
                    onChange={e => setNewFeature(e.target.value)} 
                    placeholder="Add a feature..."
                    className="flex-1"
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFeatureToPlan())}
                    data-testid="new-feature-input"
                  />
                  <Button type="button" variant="outline" onClick={addFeatureToPlan} className="rounded-xl" data-testid="add-feature-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Licensed Features - Functional Licensing */}
              <div>
                <Label className="text-sm font-medium">Licensed Features (Access Control)</Label>
                <p className="text-xs text-slate-500 mb-2">Select which app features tenants on this plan can access</p>
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto bg-slate-50 rounded-lg p-3">
                  {AVAILABLE_FEATURES.map(feature => (
                    <label 
                      key={feature.id} 
                      className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        planForm.licensed_features?.includes(feature.id) 
                          ? 'bg-emerald-50 border border-emerald-200' 
                          : 'bg-white border border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <input 
                        type="checkbox"
                        checked={planForm.licensed_features?.includes(feature.id) || false}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPlanForm(f => ({...f, licensed_features: [...(f.licensed_features || []), feature.id]}));
                          } else {
                            setPlanForm(f => ({...f, licensed_features: (f.licensed_features || []).filter(id => id !== feature.id)}));
                          }
                        }}
                        className="mt-1"
                      />
                      <div>
                        <p className="text-xs font-medium text-slate-800">{feature.name}</p>
                        <p className="text-[10px] text-slate-500">{feature.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {planForm.licensed_features?.length || 0} of {AVAILABLE_FEATURES.length} features selected
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditPlanDialog(null)} className="flex-1 rounded-xl">
                  Cancel
                </Button>
                <Button onClick={handleSavePlan} disabled={processing} className="flex-1 bg-slate-800 hover:bg-slate-900 text-white rounded-xl" data-testid="save-plan-btn">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save Plan
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Login As Dialog */}
      <Dialog open={!!loginAsDialog} onOpenChange={() => setLoginAsDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-500" />
              Login as Tenant
            </DialogTitle>
            <DialogDescription className="sr-only">Support mode login</DialogDescription>
          </DialogHeader>
          {loginAsDialog && (
            <div className="space-y-4">
              {/* Warning Banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-amber-700 font-semibold mb-1">
                  <AlertCircle className="w-4 h-4" />
                  Support Mode
                </div>
                <p className="text-sm text-amber-600">
                  All actions while logged in as this tenant will be recorded in the audit log.
                </p>
              </div>

              {/* Tenant Info */}
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-500">Tenant</p>
                <p className="font-bold text-slate-800">{loginAsDialog.name}</p>
                <p className="text-xs text-slate-400 mt-1">{loginAsDialog.owner_email}</p>
              </div>

              {/* Reason */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Reason for Access</Label>
                <Input
                  value={loginAsReason}
                  onChange={(e) => setLoginAsReason(e.target.value)}
                  placeholder="Support request, troubleshooting..."
                  className="mt-1.5"
                  data-testid="login-as-reason"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setLoginAsDialog(null)} className="flex-1 rounded-xl">
                  Cancel
                </Button>
                <Button 
                  onClick={handleLoginAs} 
                  disabled={processing} 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                  data-testid="confirm-login-as-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Continue as {loginAsDialog.name?.split(' ')[0]}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformAdminPage;
